import React, { useEffect, useState } from "react";
import { Container, Form, Button, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

// Assets
import LodingImg from "../../Assets/img/icon-spinner.svg";
import UserActionPagesTemplate from "../../Template/UserActionPagesTemplate";
// Styles
import "../../Assets/scss/components/TwoFactorVerify.scss";
import DashboardLogo from "../../Components/DashboardLogo";
import aioaLogo from "../../Assets/img/all-in-one-accessibility-logo.svg";
import DashboardLanguageButton from "../../Components/LanguageButton";
import DashboardFooter from "../../Components/Footer";
// APIs and Services
import { verifyLoginCode } from "../../Api/TwoFactorAuthAPI";
import { CompletePost2FALoginService } from "../../Services/UserAction/CompletePost2FALoginService";

// Redux
import { SET_MESSAGE, SET_2FA_VERIFIED } from "../../Actions/Types";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import LoginFooter from "../../Components/LoginFooter";

interface TwoFactorVerifyProps {
  woocommerceToken?: string;
}

const TwoFactorVerify: React.FC<TwoFactorVerifyProps> = ({
  woocommerceToken,
}) => {
  // Component state
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  // Hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Redux store
  const { accessToken, user } = useSelector((store: StoreProptypes) => store);
  /**
   * Handle code submission for 2FA or recovery verification
   */
  const handleVerify = async () => {
    if (loading) return;

    const trimmedCode = code.trim();

    // Validate 6-digit OTP or 8–24 character alphanumeric recovery code
    const isValidOTP = /^\d{6}$/.test(trimmedCode);
    const isValidRecovery = /^[a-zA-Z0-9]{8,24}$/.test(trimmedCode);

    if (!isValidOTP && !isValidRecovery) {
      setError(t("Please enter a valid 6-digit code or recovery code."));
      return;
    }

    // Clear error and show loader
    setLoading(true);
    setError("");

    try {
      // Call verification API
      const response = await verifyLoginCode({
        token: accessToken,
        code: trimmedCode,
      });

      // If verification fails
      if (!response?.status) {
        setError(t("Verification failed. Please try again."));
      } else {
        // Mark as verified
        setIsVerified(true);

        // Dispatch success state to Redux
        dispatch({
          type: SET_2FA_VERIFIED,
          payload: { toast_message: response.message, toast_type: "success" },
        });
        dispatch({
          type: SET_MESSAGE,
          payload: { toast_message: response.message, toast_type: "success" },
        });

        // Proceed to next step in login flow
        await CompletePost2FALoginService(
          accessToken || "",
          user,
          dispatch,
          navigate,
          t,
          woocommerceToken
        );
      }
    } catch {
      // Catch any request error
      setError(t("Error verifying 2FA. Please try again."));
    }

    setLoading(false);
  };

  return (
    <UserActionPagesTemplate>
      <>
        <Container className="two-fa-verify d-flex flex-column align-items-center">
          {/* Fullscreen loader during verification */}
          {loading && (
            <div
              role="alert"
              className="aioa_dashboard-fullscreen-loader"
              aria-live="polite"
            >
              <div className="aioa_dashboard-loader">
                <img src={LodingImg} alt="Loading" />
                <span className="visually-hidden">
                  {t("Loading page. Please Do not refresh the page")}
                </span>
              </div>
            </div>
          )}

          {/* Logo */}
          <div className="aioa_dashboard-header-domain-wrapper mb-5 w-75 top-0">
            {/* <DashboardLogo /> */}
            <img src={aioaLogo} alt="All in One Accessibility Logo" />
          </div>
          <h3 className="mt-4">{t("Two Factor authentication")}</h3>
          {/* Verification form */}
          <Form
            onSubmit={(e) => {
              e.preventDefault(); // Prevent form refresh
              handleVerify(); // Trigger verification
            }}
            className="verify-form mt-5 w-100"
          >
            {/* Input field for 6-digit or recovery code */}
            <Form.Group controlId="form2faCode">
              <Form.Label>
                {t("Enter an authenticator app code or recovery code")}
              </Form.Label>
              <Form.Control
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={24}
                autoComplete="one-time-code"
              />
            </Form.Group>

            {/* Help message */}
            <Form.Text className="text-muted">
              {t(
                "Enter the 6-digit code from your two-factor app, or use a recovery code if you’ve lost your device."
              )}
            </Form.Text>

            {/* Error message */}
            {error && (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            )}

            {/* Submit button */}
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              className="w-100 mt-4"
            >
              {loading ? t("Verifying...") : t("Verify code")}
            </Button>
          </Form>

        </Container>
        <LoginFooter />
      </>
    </UserActionPagesTemplate>
  );
};

export default TwoFactorVerify;
